import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, Lock, Star } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const HERO_BG =
  "https://images.unsplash.com/photo-1694261321131-8157dce8e288?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxkYXJrJTIwYWJzdHJhY3QlMjAzZCUyMHRleHR1cmV8ZW58MHx8fHwxNzc3MjI4NzkzfDA&ixlib=rb-4.1.0&q=85";
const TEXTURE =
  "https://images.pexels.com/photos/12564248/pexels-photo-12564248.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TESTIMONIALS = [
  {
    name: "Marcus Chen",
    role: "Founder, Lever Studio",
    avatar:
      "https://images.unsplash.com/photo-1769636929231-3cd7f853d038?crop=entropy&cs=srgb&fm=jpg&q=85",
    quote:
      "Closed my first $4K retainer on day 12. The frameworks are blunt, current, and they actually convert.",
  },
  {
    name: "Priya Raghavan",
    role: "Brand Strategist",
    avatar:
      "https://images.pexels.com/photos/262391/pexels-photo-262391.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    quote:
      "I rewrote my positioning on day 3 and tripled my reply rate. This isn't theory—it's an operator's manual.",
  },
  {
    name: "Daniel Akpan",
    role: "Independent Consultant",
    avatar:
      "https://images.pexels.com/photos/36712225/pexels-photo-36712225.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    quote:
      "Coaching tier is a cheat code. Specialist call gave me a real strategy I'm still running 6 months later.",
  },
];

const FAQS = [
  { q: "Who is this program for?", a: "Operators, freelancers and consultants who want to package, position, and sell a premium service in 14 days—without bloated theory." },
  { q: "How much time per day?", a: "Plan for 45-90 minutes a day. Lessons are sharp; the work is what creates the leverage." },
  { q: "What's the difference between Premium and Coaching?", a: "Coaching includes everything in Premium plus a 1:1 specialist call, personalized brand audit, and outreach scripts to land your first client." },
  { q: "Why is Coaching limited to 5 spots per month?", a: "Each Coaching seat includes a real 1:1 call and personalized work. We cap it at 5 per month so every operator gets focused attention." },
  { q: "Do I get lifetime access?", a: "You get lifetime access to your purchased materials, including all future curriculum updates." },
];

export default function LandingPage() {
  const [plans, setPlans] = useState([]);
  const [curriculum, setCurriculum] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans));
    api.get("/curriculum").then((r) => setCurriculum(r.data.days));
  }, []);

  const handleCheckout = async (planId) => {
    if (!user || user === false) {
      toast.message("Sign in to continue", {
        description: "Create an account first—takes 10 seconds.",
      });
      navigate("/register?next=pricing");
      return;
    }
    try {
      setLoadingPlan(planId);
      const { data } = await api.post("/checkout/session", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      // Stripe Checkout blocks being loaded inside an iframe (X-Frame-Options: DENY).
      // If we're inside an iframe (preview), open Stripe in a new tab; otherwise navigate.
      const inIframe = window.self !== window.top;
      if (inIframe) {
        const win = window.open(data.url, "_blank", "noopener,noreferrer");
        if (!win) {
          toast.error("Pop-ups are blocked. Please allow pop-ups for this site.");
        }
        setLoadingPlan(null);
      } else {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error("Checkout failed. Please try again.");
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden grain" data-testid="hero-section">
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.85) 60%, #0a0a0a 100%), url('${HERO_BG}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-32 lg:pt-32 lg:pb-44">
          <div className="overline mb-6 fade-up">14 Days · Brandscaling Studio</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-medium tracking-tighter leading-[0.95] max-w-5xl fade-up">
            Build a premium brand
            <br />
            <span className="text-zinc-400">that sells</span> in 14 days.
          </h1>
          <p className="mt-8 text-lg text-zinc-400 max-w-2xl leading-relaxed fade-up">
            A daily operator's program for consultants, freelancers and founders.
            Position. Productize. Land paying clients—without burning the next two years figuring it out.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 fade-up">
            <a href="#pricing" className="btn-primary" data-testid="hero-cta-primary">
              Choose your plan <ArrowRight size={18} />
            </a>
            <a href="#curriculum" className="btn-secondary" data-testid="hero-cta-secondary">
              See the 14 days
            </a>
          </div>

          <div className="mt-16 flex flex-wrap gap-x-10 gap-y-4 text-sm text-zinc-500">
            <div className="flex items-center gap-2"><Check size={16} className="text-zinc-300" /> 1,200+ operators trained</div>
            <div className="flex items-center gap-2"><Check size={16} className="text-zinc-300" /> Coaching limited to 5 spots / month</div>
            <div className="flex items-center gap-2"><Check size={16} className="text-zinc-300" /> Lifetime access</div>
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section id="curriculum" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="curriculum-section">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-5">
            <div className="overline mb-4">The 14 Days</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium">
              A daily playbook,<br />not another course.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-zinc-400 text-lg leading-relaxed">
              Every day delivers one focused asset, one decision, and one shipped output.
              By day 14 you have a positioned brand, a productized offer, and a working
              outreach engine—not just notes.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {curriculum.map((d, idx) => {
            const isMilestone = d.day === 7 || d.day === 14;
            return (
              <div
                key={d.day}
                className={`card-surface p-7 relative overflow-hidden ${
                  isMilestone ? "lg:col-span-2 bg-[#1a1a1d]" : ""
                }`}
                style={{ animationDelay: `${idx * 30}ms` }}
                data-testid={`curriculum-day-${d.day}`}
              >
                {isMilestone && (
                  <div
                    className="absolute inset-0 opacity-10 -z-0"
                    style={{ backgroundImage: `url('${TEXTURE}')`, backgroundSize: "cover" }}
                  />
                )}
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-mono text-xs text-zinc-500">DAY {String(d.day).padStart(2, "0")}</span>
                    {isMilestone && (
                      <span className="overline !text-[10px] text-zinc-300">Milestone</span>
                    )}
                  </div>
                  <h3 className="font-display text-xl font-medium mb-2">{d.title}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{d.summary}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="pricing-section">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="overline mb-4">Pricing</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium">
            Pick the depth of help<br />you actually need.
          </h2>
          <p className="text-zinc-400 mt-4">One payment. Lifetime access. No upsells in your inbox.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const isCoaching = plan.id === "coaching";
            const isPremium = plan.id === "premium";
            const isHighlighted = isPremium;
            return (
              <div
                key={plan.id}
                className={`relative p-8 lg:p-10 flex flex-col ${
                  isHighlighted
                    ? "bg-[#1a1a1d] border border-white/20 ring-1 ring-white/10 shadow-[0_0_60px_rgba(255,255,255,0.05)]"
                    : "card-surface"
                }`}
                data-testid={`pricing-card-${plan.id}`}
              >
                {isPremium && (
                  <span className="absolute -top-3 left-8 bg-white text-black text-xs font-medium px-3 py-1 font-mono tracking-wider">
                    MOST POPULAR
                  </span>
                )}
                {isCoaching && (
                  <span
                    className="absolute -top-3 left-8 bg-[#1a1a1d] text-zinc-100 text-xs font-medium px-3 py-1 font-mono tracking-wider border border-white/20"
                    data-testid="coaching-limited-badge"
                  >
                    LIMITED · 5 SPOTS/MONTH
                  </span>
                )}
                <div className="overline mb-3">{plan.name}</div>
                <div className="mb-2">
                  <span className="font-display text-5xl font-medium">${plan.price.toFixed(0)}</span>
                  <span className="text-zinc-500 ml-2">USD</span>
                </div>
                <p className="text-sm text-zinc-400 mb-8 min-h-[40px]">{plan.tagline}</p>
                <ul className="space-y-3 mb-10 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex gap-3 text-sm text-zinc-300">
                      <Check size={16} className="text-zinc-400 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleCheckout(plan.id)}
                  disabled={loadingPlan === plan.id}
                  className={
                    isHighlighted
                      ? "btn-primary justify-center w-full"
                      : "btn-secondary justify-center w-full"
                  }
                  data-testid={`pricing-${plan.id}-checkout-button`}
                >
                  {loadingPlan === plan.id ? "Redirecting..." : `Get ${plan.name}`}
                  {loadingPlan !== plan.id && <ArrowRight size={16} />}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t border-white/5" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <div className="overline mb-4">Proof</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium">
                Operators who shipped<br />and got paid.
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t) => (
              <div
                key={t.name}
                className="backdrop-blur-2xl bg-white/[0.03] border border-white/10 p-7"
                data-testid={`testimonial-${t.name.split(" ")[0].toLowerCase()}`}
              >
                <div className="flex gap-1 mb-5 text-zinc-300">
                  {[...Array(5)].map((_, i) => (<Star key={i} size={14} fill="currentColor" />))}
                </div>
                <p className="text-zinc-200 leading-relaxed mb-7">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full bg-zinc-800 ring-1 ring-white/10"
                    style={{ backgroundImage: `url('${t.avatar}')`, backgroundSize: "cover", backgroundPosition: "center" }}
                  />
                  <div>
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-xs text-zinc-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="faq-section">
        <div className="overline mb-4 text-center">Frequently asked</div>
        <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium text-center mb-14">
          Questions, answered.
        </h2>
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="border-b border-white/10"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="font-display text-lg font-medium text-left hover:no-underline py-6">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-zinc-400 leading-relaxed pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="divider my-16"></div>
        <div className="text-center">
          <h3 className="font-display text-3xl font-medium mb-4">Ready to compress 2 years into 14 days?</h3>
          <Link to="/register" className="btn-primary inline-flex" data-testid="faq-cta-register">
            Start now <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
